import { useEffect, useState } from 'react';
import { employesApi } from '../../api/resources';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { dateHeureSuisse } from '../../lib/acces';
import { C } from '../../theme/palette';

/**
 * Ce que la fiche dit de la signature du contrat par l'employé.
 *
 * <p>« Contrat signé le … » avec le PDF figé à télécharger ; « contrat
 * modifié, nouvelle signature demandée » quand la direction a changé les
 * conditions depuis ; « en attente de signature » tant que l'employé n'a pas
 * signé dans son espace. Les signatures précédentes restent téléchargeables.
 *
 * @param {object} props propriétés du composant
 * @param {number} props.employeId fiche concernée
 * @returns {import('react').ReactElement|null} le suivi, ou rien tant qu'il n'est pas chargé
 */
export function ContratSigne({ employeId }) {
  const [etat, setEtat] = useState(null);

  useEffect(() => {
    if (typeof employeId !== 'number' || typeof employesApi.signatureContrat !== 'function') {
      return undefined;
    }
    let annule = false;
    employesApi
      .signatureContrat(employeId)
      .then((e) => {
        if (!annule) setEtat(e);
      })
      .catch(() => {
        if (!annule) setEtat(null);
      });
    return () => {
      annule = true;
    };
  }, [employeId]);

  if (!etat) return null;

  const fond = etat.nouvelleSignatureDemandee ? C.amberL : etat.signe ? C.greenL : C.navyXL;
  const bord = etat.nouvelleSignatureDemandee ? C.amber : etat.signe ? C.green : C.border;
  const anciennes = (etat.signatures || []).slice(1);

  return (
    <div
      data-testid="contrat-signe"
      style={{
        background: fond,
        border: '1px solid ' + bord,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 9.5,
        lineHeight: 1.5,
      }}
    >
      {!etat.signe && (
        <div style={{ fontWeight: 800, color: C.grey }}>
          ✍️ Contrat en attente de signature par l’employé, dans son espace.
        </div>
      )}
      {etat.signe && (
        <div style={{ fontWeight: 800, color: etat.nouvelleSignatureDemandee ? C.amber : C.green }}>
          {etat.nouvelleSignatureDemandee
            ? '⚠️ Contrat modifié, nouvelle signature demandée'
            : '✓ Contrat signé le ' + dateHeureSuisse(etat.signeLe)}
        </div>
      )}
      {etat.signe && etat.nouvelleSignatureDemandee && (
        <div style={{ fontSize: 8.5, color: C.grey, marginTop: 2 }}>
          Dernière signature le {dateHeureSuisse(etat.signeLe)} : les conditions ont changé depuis.
          L’employé est invité à signer de nouveau ; l’ancien contrat signé est conservé.
        </div>
      )}
      {etat.signe && (
        <div style={{ marginTop: 7 }}>
          <BoutonDocument
            chemin={`/employes/${employeId}/contrat/signature/document`}
            label="📄 Contrat signé (PDF)"
            color={C.green}
            sm
          />
        </div>
      )}
      {anciennes.length > 0 && (
        <div style={{ marginTop: 7, fontSize: 8.5, color: C.grey }}>
          Signatures précédentes :
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 }}>
            {anciennes.map((s) => (
              <BoutonDocument
                key={s.id}
                chemin={`/employes/${employeId}/contrat/signature/${s.id}/document`}
                label={'📄 ' + dateHeureSuisse(s.signeLe)}
                color={C.grey}
                sm
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
